import SortIcon from '@/components/icons/SortIcon'
import { SortBy } from '@/constant/common'
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Stack } from '@mui/system'
import { FC } from 'react'
import { EtZone } from '@/types'
import { useLocation } from 'react-router-dom'
import classes from './style.module.scss'
import useTranslation from '@/hooks/useTranslation'
import { usePermissionAccess } from '@/features/auth/hooks/usePermissionAccess'

interface EtzTableProps {
  rows: EtZone[]
  onEditItem: (id: string) => void
  onDeleteItem?: (id: string, interfaceId: string) => void
  handleDesc: (sortBy: SortBy) => void
  handleEsc: (sortBy: SortBy) => void
}

const EtzTable: FC<EtzTableProps> = ({
  rows,
  onEditItem,
  onDeleteItem,
  handleDesc,
  handleEsc,
}) => {
  const { t } = useTranslation()
  const { isViewOnly } = usePermissionAccess()

  const handleEditItem = (id: string) => {
    onEditItem(id)
  }

  const handleModalDelete = (id: string, interfaceId: string) => {
    onDeleteItem && onDeleteItem(id, interfaceId)
  }

  const location = useLocation()
  const searchParamsFromURL = new URLSearchParams(location.search)

  return (
    <Stack flexGrow="1">
      <TableContainer component={Paper} elevation={0}>
        <Table
          sx={{
            minWidth: 650,
          }}
          aria-label="simple table"
        >
          <TableHead>
            <TableRow>
              <TableCell>
                <Stack direction="row" spacing={1}>
                  <SortIcon
                    handleDesc={() => handleDesc(SortBy.ID)}
                    handleEsc={() => handleEsc(SortBy.ID)}
                    sortBy={SortBy.ID}
                    isDesc={
                      searchParamsFromURL.get('SortBy') === SortBy.ID &&
                      searchParamsFromURL.get('SortDirection') === 'desc'
                    }
                    isSorted={
                      searchParamsFromURL.get('SortBy') === SortBy.ID &&
                      searchParamsFromURL.get('SortDirection') === 'asc'
                    }
                  />
                  <Typography className={classes.txtEtZoneHead}>ID</Typography>
                </Stack>
              </TableCell>
              <TableCell
                sx={{
                  minWidth: '222.25px',
                  height: '40px',
                  borderRight: '1px solid #D7D7D7',
                }}
              >
                <Stack direction="row" spacing={1}>
                  <SortIcon
                    handleDesc={() => handleDesc(SortBy.Name)}
                    handleEsc={() => handleEsc(SortBy.Name)}
                    sortBy={SortBy.Name}
                    isDesc={
                      searchParamsFromURL.get('SortBy') === SortBy.Name &&
                      searchParamsFromURL.get('SortDirection') === 'desc'
                    }
                    isSorted={
                      searchParamsFromURL.get('SortBy') === SortBy.Name &&
                      searchParamsFromURL.get('SortDirection') === 'asc'
                    }
                  />
                  <Typography className={classes.txtEtZoneHead}>
                    {t('formsLabelsMicroClimate')}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell
                sx={{
                  minWidth: '332.25px',
                  height: '40px',
                }}
              >
                {t('mapView.content.soilMoistureSensor')}
              </TableCell>
              <TableCell
                sx={{
                  minWidth: '172.25px',
                }}
              >
                {t('interfaceTableHeaderAction')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ backgroundColor: 'common.white' }}>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell
                  component="th"
                  scope="row"
                  className={classes.tableTh}
                >
                  {row.id}
                </TableCell>
                <TableCell
                  component="th"
                  scope="row"
                  sx={{
                    borderRight: '1px solid #D7D7D7',
                  }}
                  className={classes.tableTh}
                >
                  {row.name}
                </TableCell>
                <TableCell>{row.soilSensor?.customName || '-'}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing="10px">
                    <Typography
                      sx={{
                        fontSize: '13px',
                        fontWeight: '500',
                        lineHeight: '100%',
                        cursor: isViewOnly ? 'not-allowed' : 'pointer',
                        color: isViewOnly ? 'black' : 'primary.main',
                        opacity: isViewOnly ? 0.2 : 1,
                        pointerEvents: isViewOnly ? 'none' : 'auto',
                      }}
                      onClick={() => handleEditItem(row.id)}
                    >
                      {t('actionEdit')}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '13px',
                        fontWeight: '500',
                        lineHeight: '100%',
                        cursor: isViewOnly ? 'not-allowed' : 'pointer',
                        color: isViewOnly ? 'black' : 'grey.600',
                        opacity: isViewOnly ? 0.2 : 1,
                        pointerEvents: isViewOnly ? 'none' : 'auto',
                      }}
                      onClick={() =>
                        handleModalDelete(row.id, row.interfaceId + '')
                      }
                    >
                      {t('actionDelete')}
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  )
}

export default EtzTable
